import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CategoryIcon from "@mui/icons-material/Category";
import {
  Box,
  CircularProgress,
  Container,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import { type Category, type SubCategory } from "@entities";
import { categoryRepo, subCategoryRepo } from "@repositories";
import { compareBySortOrder } from "@shared/helpers/category-sort";
import { WEME_ORGANIZATION_ID } from "@shared/helpers/constants";
import { Roles } from "@shared/Roles";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type FC } from "react";
import { Navigate } from "react-router-dom";
import { remult } from "remult";
import { useAlert } from "../../contexts/AlertProvider";

const QUERY_KEY = ["admin-category-visibility", WEME_ORGANIZATION_ID];

function moveRow<T extends { id: string; sortOrder?: number }>(
  rows: T[],
  index: number,
  direction: -1 | 1,
): T[] | null {
  const target = index + direction;
  if (target < 0 || target >= rows.length) return null;
  const next = rows.slice();
  const [moved] = next.splice(index, 1);
  next.splice(target, 0, moved);
  return next.map((row, order) => ({ ...row, sortOrder: order + 1 }));
}

/**
 * Marketplace category visibility.
 * `isHidden` = aid-request form; `isHiddenFromPreferences` = volunteer prefs / feed chips.
 * Subcategory `isLinkOnly` stays out of those lists unless a link includes its id.
 */
const CategoryVisibilityPage: FC = () => {
  const { setAlertText, setSeverity, setTime } = useAlert();
  const queryClient = useQueryClient();
  const canEdit = remult.isAllowed(Roles.SuperAdmins);

  const { data: categories = [], isLoading, isError, refetch } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async () => {
      const rows = (await categoryRepo.find({
        where: { organizationId: WEME_ORGANIZATION_ID },
        orderBy: { sortOrder: "asc" },
      })) as Category[];
      const ids = rows.map((category) => category.id);
      const subCategories = ids.length
        ? ((await subCategoryRepo.find({
            where: { categoryId: { $in: ids } },
            orderBy: { sortOrder: "asc" },
          })) as SubCategory[])
        : [];
      const byCategory = new Map<string, SubCategory[]>();
      for (const sub of subCategories) {
        const list = byCategory.get(sub.categoryId) ?? [];
        list.push(sub);
        byCategory.set(sub.categoryId, list);
      }
      return rows.sort(compareBySortOrder).map((category) => ({
        ...category,
        subCategories: (byCategory.get(category.id) ?? []).sort(compareBySortOrder),
      }));
    },
    enabled: canEdit,
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      id: string;
      isHidden?: boolean;
      isHiddenFromPreferences?: boolean;
    }) => {
      const { id, ...patch } = payload;
      await categoryRepo.update(id, patch);
      return payload;
    },
    onSuccess: (payload) => {
      queryClient.setQueryData<Category[]>(QUERY_KEY, (prev) =>
        (prev ?? []).map((c) =>
          c.id === payload.id
            ? {
                ...c,
                ...(payload.isHidden !== undefined
                  ? { isHidden: payload.isHidden }
                  : {}),
                ...(payload.isHiddenFromPreferences !== undefined
                  ? {
                      isHiddenFromPreferences:
                        payload.isHiddenFromPreferences,
                    }
                  : {}),
              }
            : c,
        ),
      );
      setSeverity("success");
      setTime(1500);
      setAlertText("עודכן");
    },
    onError: () => {
      setSeverity("warning");
      setTime(2500);
      setAlertText("שמירה נכשלה - נסו שוב");
    },
  });

  const updateSubMutation = useMutation({
    mutationFn: async (payload: { id: string; isLinkOnly: boolean }) => {
      await subCategoryRepo.update(payload.id, {
        isLinkOnly: payload.isLinkOnly,
      });
      return payload;
    },
    onSuccess: (payload) => {
      queryClient.setQueryData<Category[]>(QUERY_KEY, (prev) =>
        (prev ?? []).map((category) => ({
          ...category,
          subCategories: (category.subCategories ?? []).map((sub) =>
            sub.id === payload.id
              ? { ...sub, isLinkOnly: payload.isLinkOnly }
              : sub,
          ),
        })),
      );
      setSeverity("success");
      setTime(1500);
      setAlertText("עודכן");
    },
    onError: () => {
      setSeverity("warning");
      setTime(2500);
      setAlertText("שמירה נכשלה - נסו שוב");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async (payload: {
      kind: "category" | "subcategory";
      rows: { id: string; sortOrder: number }[];
    }) => {
      await Promise.all(
        payload.rows.map((row) =>
          payload.kind === "category"
            ? categoryRepo.update(row.id, { sortOrder: row.sortOrder })
            : subCategoryRepo.update(row.id, { sortOrder: row.sortOrder }),
        ),
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      setSeverity("success");
      setTime(1500);
      setAlertText("הסדר עודכן");
    },
    onError: () => {
      setSeverity("warning");
      setTime(2500);
      setAlertText("שמירת הסדר נכשלה - נסו שוב");
    },
  });

  const reorderBusy = reorderMutation.isPending;

  if (!canEdit) {
    return <Navigate to="/" replace />;
  }

  if (isError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <Stack spacing={2} alignItems="center">
          <Typography color="text.secondary">
            לא הצלחנו לטעון קטגוריות
          </Typography>
          <Typography
            component="button"
            color="primary"
            sx={{
              border: "none",
              background: "none",
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => {
              void refetch();
            }}
          >
            נסו שוב
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3, dir: "rtl", overflow: "auto" }}>
      <Paper sx={{ p: { xs: 2.5, sm: 3.5 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <CategoryIcon color="primary" />
            <Box>
              <Typography variant="h5" fontWeight={700}>
                נראות קטגוריות
              </Typography>
              <Typography color="text.secondary" fontSize="0.9rem">
                קטגוריות ראשיות למתנדב ולמבקש סיוע. החצים קובעים מה מוצג קודם, גם בתת-קטגוריות. תת-קטגוריה עם "רק בקישור" לא מופיעה ברשימה הרגילה.
              </Typography>
            </Box>
          </Stack>

          <Divider />

          <Stack spacing={1.5}>
            {categories.length === 0 ? (
              <Typography color="text.secondary">
                לא נמצאו קטגוריות לארגון השוק
              </Typography>
            ) : (
              categories.map((category, categoryIndex) => {
                const rowBusy =
                  updateMutation.isPending &&
                  updateMutation.variables?.id === category.id;
                return (
                <Paper
                  key={category.id}
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "background.default",
                  }}
                >
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography fontWeight={700}>{category.name}</Typography>
                      <Stack direction="row">
                        <IconButton
                          aria-label="הצג קודם"
                          size="small"
                          disabled={reorderBusy || categoryIndex === 0}
                          onClick={() => {
                            const next = moveRow(categories, categoryIndex, -1);
                            if (!next) return;
                            reorderMutation.mutate({
                              kind: "category",
                              rows: next.map((row) => ({
                                id: row.id,
                                sortOrder: row.sortOrder,
                              })),
                            });
                          }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          aria-label="הצג אחר כך"
                          size="small"
                          disabled={reorderBusy || categoryIndex === categories.length - 1}
                          onClick={() => {
                            const next = moveRow(categories, categoryIndex, 1);
                            if (!next) return;
                            reorderMutation.mutate({
                              kind: "category",
                              rows: next.map((row) => ({
                                id: row.id,
                                sortOrder: row.sortOrder,
                              })),
                            });
                          }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      alignItems={{ xs: "stretch", sm: "center" }}
                      justifyContent="space-between"
                    >
                      <FormControlLabel
                        sx={{ marginInlineStart: 0, flexShrink: 0 }}
                        control={
                          <Switch
                            checked={!!category.isHiddenFromPreferences}
                            disabled={rowBusy}
                            onChange={(_, checked) =>
                              updateMutation.mutate({
                                id: category.id,
                                isHiddenFromPreferences: checked,
                              })
                            }
                          />
                        }
                        label="הסתר מהעדפות / מסנני פיד"
                      />
                      <FormControlLabel
                        sx={{ marginInlineStart: 0, flexShrink: 0 }}
                        control={
                          <Switch
                            checked={!!category.isHidden}
                            disabled={rowBusy}
                            onChange={(_, checked) =>
                              updateMutation.mutate({
                                id: category.id,
                                isHidden: checked,
                              })
                            }
                          />
                        }
                        label="הסתר מטופס בקשת סיוע"
                      />
                    </Stack>
                    {(category.subCategories ?? []).length > 0 && (
                      <Stack spacing={0.75} sx={{ pt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          תת-קטגוריות
                        </Typography>
                        {(category.subCategories ?? []).map((sub, subIndex, subs) => {
                          const subBusy =
                            updateSubMutation.isPending &&
                            updateSubMutation.variables?.id === sub.id;
                          return (
                            <Stack
                              key={sub.id}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                            >
                              <FormControlLabel
                                sx={{ marginInlineStart: 0 }}
                                control={
                                  <Switch
                                    checked={!!sub.isLinkOnly}
                                    disabled={subBusy}
                                    onChange={(_, checked) =>
                                      updateSubMutation.mutate({
                                        id: sub.id,
                                        isLinkOnly: checked,
                                      })
                                    }
                                  />
                                }
                                label={`${sub.name} - רק בקישור`}
                              />
                              <Stack direction="row">
                                <IconButton
                                  aria-label="הצג תת-קטגוריה קודם"
                                  size="small"
                                  disabled={reorderBusy || subIndex === 0}
                                  onClick={() => {
                                    const next = moveRow(subs, subIndex, -1);
                                    if (!next) return;
                                    reorderMutation.mutate({
                                      kind: "subcategory",
                                      rows: next.map((row) => ({
                                        id: row.id,
                                        sortOrder: row.sortOrder ?? 10000,
                                      })),
                                    });
                                  }}
                                >
                                  <ArrowUpwardIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  aria-label="הצג תת-קטגוריה אחר כך"
                                  size="small"
                                  disabled={reorderBusy || subIndex === subs.length - 1}
                                  onClick={() => {
                                    const next = moveRow(subs, subIndex, 1);
                                    if (!next) return;
                                    reorderMutation.mutate({
                                      kind: "subcategory",
                                      rows: next.map((row) => ({
                                        id: row.id,
                                        sortOrder: row.sortOrder ?? 10000,
                                      })),
                                    });
                                  }}
                                >
                                  <ArrowDownwardIcon fontSize="small" />
                                </IconButton>
                              </Stack>
                            </Stack>
                          );
                        })}
                      </Stack>
                    )}
                  </Stack>
                </Paper>
                );
              })
            )}
          </Stack>
        </Stack>
      </Paper>
    </Container>
  );
};

export default CategoryVisibilityPage;
